import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

const COLLAPSED_LINE_COUNT = 4;

function lineHeightInPixels(element: HTMLElement): number {
  const styles = window.getComputedStyle(element);
  const parsed = Number.parseFloat(styles.lineHeight);
  if (Number.isFinite(parsed) && parsed > 1.5) return parsed;

  const fontSize = Number.parseFloat(styles.fontSize);
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize * 1.45 : 20;
}

function exceedsCollapsedLines(element: HTMLElement): boolean {
  const original = {
    display: element.style.display,
    overflow: element.style.overflow,
    maxHeight: element.style.maxHeight,
    webkitLineClamp: element.style.getPropertyValue('-webkit-line-clamp'),
  };

  // Measure the un-clamped block. This avoids relying on browser-specific
  // `scrollHeight` behavior for line-clamped text.
  element.style.display = 'block';
  element.style.overflow = 'visible';
  element.style.maxHeight = 'none';
  element.style.setProperty('-webkit-line-clamp', 'unset');
  const isOverflowing = element.scrollHeight > lineHeightInPixels(element) * COLLAPSED_LINE_COUNT + 1;

  element.style.display = original.display;
  element.style.overflow = original.overflow;
  element.style.maxHeight = original.maxHeight;
  if (original.webkitLineClamp) {
    element.style.setProperty('-webkit-line-clamp', original.webkitLineClamp);
  } else {
    element.style.removeProperty('-webkit-line-clamp');
  }

  return isOverflowing;
}

/**
 * Keeps descriptions compact until they genuinely exceed four rendered lines.
 * Non-string content deliberately remains unmeasured: it can contain its own
 * semantics and must never be coerced or flattened for a toast affordance.
 */
export function ToastDescription({ description }: { description: ReactNode }) {
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const [canExpand, setCanExpand] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldFocusExpandedCopy, setShouldFocusExpandedCopy] = useState(false);

  useLayoutEffect(() => {
    setIsExpanded(false);
    if (typeof description !== 'string' || !descriptionRef.current || typeof window === 'undefined') {
      setCanExpand(false);
      return;
    }

    const measure = () => {
      const element = descriptionRef.current;
      if (element) setCanExpand(exceedsCollapsedLines(element));
    };
    measure();

    const ResizeObserverConstructor = globalThis.ResizeObserver;
    const observer = ResizeObserverConstructor ? new ResizeObserverConstructor(measure) : undefined;
    observer?.observe(descriptionRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [description]);

  useLayoutEffect(() => {
    if (isExpanded && shouldFocusExpandedCopy) {
      descriptionRef.current?.focus();
      setShouldFocusExpandedCopy(false);
    }
  }, [isExpanded, shouldFocusExpandedCopy]);

  if (typeof description !== 'string') return <>{description}</>;

  return (
    <>
      <span
        ref={descriptionRef}
        className="invento-toast__description-copy"
        data-collapsed={canExpand && !isExpanded}
        tabIndex={isExpanded ? -1 : undefined}
      >
        {description}
      </span>
      {canExpand && !isExpanded ? (
        <button
          type="button"
          className="invento-toast__read-more"
          onClick={() => {
            setShouldFocusExpandedCopy(true);
            setIsExpanded(true);
          }}
          aria-label="Read full notification description"
        >
          Read more
        </button>
      ) : null}
    </>
  );
}
